/**
 * useCircuitRunStore — subscribes to the authoritative users/{uid}/_singles/circuit_run
 * doc (written only by the circuitRun callable). Derives a display countdown from
 * phaseEndsAt and BRIDGES the run into useFlowStore's exec fields so the existing UI
 * (MiniPlayer, FlowRunner, AppShell, Today) reflects the cloud-authoritative run
 * without each consumer being rewritten.
 */
import { create } from 'zustand';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase/config';
import { reconcileCircuitRun } from '../utils/circuitProxy';
import useFlowStore from './useFlowStore';
import useGamificationStore from './useGamificationStore';

/**
 * Stable per-run key for the circuit-completion bonus. Combines the circuit id with
 * the run's startedAt so re-running the SAME circuit later still earns a fresh bonus,
 * but the SAME run (across reloads/snapshots) never double-awards.
 */
function runKeyFor(run) {
  if (!run) return null;
  const circuitId = run.circuitId || run.circuitSnapshot?.name || 'circuit';
  const started = run.startedAt;
  let startedKey = '';
  if (started && typeof started.toMillis === 'function') startedKey = String(started.toMillis());
  else if (typeof started === 'number') startedKey = String(started);
  else if (started) startedKey = String(started);
  return `${circuitId}:${startedKey}`;
}

function deriveRemainingMs(run) {
  if (!run) return 0;
  if (run.status === 'paused') return run.remainingMs || 0;
  if (run.status === 'running' && run.phaseEndsAt) return Math.max(0, run.phaseEndsAt.toMillis() - Date.now());
  return 0;
}

// Map cloud status → the legacy executionState the existing UI expects.
function execStateFor(run) {
  if (!run) return 'idle';
  if (run.status === 'running') return 'running';
  if (run.status === 'paused') return 'paused';
  return 'idle';
}

/** Mirror the cloud run into useFlowStore exec fields (single source the old UI reads). */
function bridgeToFlowStore(run, remainingMs) {
  const active = run && (run.status === 'running' || run.status === 'paused');
  useFlowStore.setState({
    activeFlow: active ? run.circuitSnapshot : null,
    currentStepIndex: active ? run.currentStepIndex : 0,
    executionState: execStateFor(run),
    flowPhase: active ? run.phase : 'focus',
    flowIsPaused: run ? run.status === 'paused' : false,
    flowTimeRemaining: active ? Math.round(remainingMs / 1000) : null,
    flowActiveTaskId: active ? (run.activeTaskId || null) : null,
  });
}

const useCircuitRunStore = create((set, get) => ({
  run: null,
  remainingMs: 0,
  _unsub: null,
  _ticker: null,
  _reconciledFor: null,
  _completionAwardedFor: null, // run key we've already handed the completion bonus for (this session)
  _prevStatus: null,           // last observed run.status — used to detect a live completion edge

  isActive() { const r = get().run; return !!r && (r.status === 'running' || r.status === 'paused'); },

  init(uid) {
    if (!uid) return;
    get().cleanup();
    const ref = doc(db, 'users', uid, '_singles', 'circuit_run');
    const unsub = onSnapshot(ref, (snap) => {
      const run = snap.exists() ? snap.data() : null;
      const prevStatus = get()._prevStatus;
      const remainingMs = deriveRemainingMs(run);
      set({ run, remainingMs, _prevStatus: run ? run.status : null });
      bridgeToFlowStore(run, remainingMs);
      // Circuit-completion bonus: fire ONLY on an OBSERVED transition into 'completed'
      // (running|paused → completed) that we witnessed live this session — NEVER on an
      // initial already-'completed' snapshot. The run doc stays 'completed' until the next
      // start/stop, so a bare completed snapshot fires on every re-subscribe / device switch /
      // cache-cleared re-login; granting there would double-award. Triple-guarded: observed
      // edge + within-session key + the gamification store's persisted awarded-run marker.
      if (run && run.status === 'completed' && (prevStatus === 'running' || prevStatus === 'paused')) {
        const key = runKeyFor(run);
        if (key && get()._completionAwardedFor !== key) {
          set({ _completionAwardedFor: key });
          useGamificationStore.getState().grantCircuitCompletion(key);
        }
      }
      // One reconcile if a running deadline already elapsed (e.g. opened after a phase ended).
      if (run && run.status === 'running' && run.phaseEndsAt && run.phaseEndsAt.toMillis() <= Date.now()) {
        const key = `${run.epoch}`;
        if (get()._reconciledFor !== key) { set({ _reconciledFor: key }); reconcileCircuitRun().catch(() => {}); }
      }
    }, () => { /* transient errors: keep last state */ });

    // 1s display ticker (derived, NOT authoritative).
    const ticker = setInterval(() => {
      const run = get().run;
      if (!run || run.status !== 'running') return;
      const remainingMs = deriveRemainingMs(run);
      set({ remainingMs });
      bridgeToFlowStore(run, remainingMs);
      // When the derived countdown hits 0, ask the server to advance (idempotent + epoch-guarded).
      if (remainingMs <= 0) {
        const key = `adv-${run.epoch}`;
        if (get()._reconciledFor !== key) { set({ _reconciledFor: key }); reconcileCircuitRun().catch(() => {}); }
      }
    }, 1000);

    set({ _unsub: unsub, _ticker: ticker });
  },

  cleanup() {
    const { _unsub, _ticker } = get();
    if (_unsub) _unsub();
    if (_ticker) clearInterval(_ticker);
    set({ run: null, remainingMs: 0, _unsub: null, _ticker: null, _reconciledFor: null, _completionAwardedFor: null, _prevStatus: null });
  },
}));

export default useCircuitRunStore;
